import {type Key, type RefObject, useCallback, useRef} from 'react'

/**
 * Store refs to many instances in one convenient place. Useful when rendering a list of items.
 *
 * @example
 * ```jsx
 * const itemsRef = useMultiRef<string, HTMLLIElement>()
 *
 * useEffect(() => {
 *   itemsRef("a").current?.focus()
 * }, [])
 *
 * return <ul>
 *   {items.map(item => <li key={item.id} ref={itemsRef(item.id)}>{item.text}</li>)}
 * </ul>
 * ```
 */
export function useMultiRef<K extends Key, V>() {
  type Store = Map<K, RefObject<V | null>>

  const storeRef = useRef<Store>(null)

  return useCallback((key: K) => {
    const store: Store = (storeRef.current ??= new Map())

    const refForKey = store.get(key) ?? {current: null}
    store.set(key, refForKey)

    return refForKey
  }, [])
}
